import { defineNuxtPlugin } from '#app'
import { PostgreSQLDataSyncType } from '@baserow/modules/database/dataSyncTypes'

import {
  ArchivosTableExporterType,
  ExcelTableExporterType,
  JSONTableExporterType,
  XMLTableExporterType,
} from '@extras_mg/exporterTypes'
import {
  BordeIzquierdoColorViewDecoratorType,
  FondoColorViewDecoratorType,
} from '@extras_mg/viewDecorators'
import {
  ColorCondicionalValueProviderType,
  ColorPorSeleccionValueProviderType,
} from '@extras_mg/decoratorValueProviders'
import { GraficaWidgetType } from '@extras_mg/widgetTypes'
import { BateriaViewAggregationType } from '@extras_mg/viewAggregationTypes'
import {
  SoloAsignadosMGOwnershipType,
  VistaPersonalMGOwnershipType,
} from '@extras_mg/viewOwnershipTypes'
import {
  CalendarioMGViewType,
  KanbanMGViewType,
  TimelineMGViewType,
} from '@extras_mg/viewTypes'
import { FormViewModeEncuestaType } from '@extras_mg/formViewModeTypes'
import { LectorMGRoleType, SinAccesoMGRoleType } from '@extras_mg/roleTypes'
import {
  BasesOcultasMGPermissionManagerType,
  LectorMGPermissionManagerType,
  PermisosDeCampoMGPermissionManagerType,
  RolesMGPermissionManagerType,
} from '@extras_mg/permissionManagerTypes'
import { PermisosDeColumnaContextItemType } from '@extras_mg/fieldContextItemTypes'
import { AccesoABasesWorkspaceSettingsPageType } from '@extras_mg/workspaceSettingsPageTypes'
import { RegistroAdminType, SsoAdminType } from '@extras_mg/adminTypes'
import { GoogleMGAuthProviderType } from '@extras_mg/authProviderTypes'
import { RolesMGMembersPagePluginType } from '@extras_mg/membersPagePluginTypes'
import { DatabaseMGApplicationType } from '@extras_mg/applicationTypes'
import { FilasQueEntranAVistaWebhookEventType } from '@extras_mg/webhookEventTypes'
import {
  EnterpriseSinCandadosPlugin,
  ExtrasMGPlugin,
} from '@extras_mg/plugins'
import { instalarInterceptor } from '@extras_mg/supervision'
import { WordMGFilePreview } from '@extras_mg/filePreviewTypes'

export default defineNuxtPlugin({
  name: 'extras-mg',
  // 'premium' no es opcional: premium registra exportadores con estos mismos
  // nombres de tipo y hay que correr despues para poder quitarlos. Sin esto el
  // orden queda al azar y aparecen los suyos, que solo pintan el anuncio de
  // pago. Es la misma trampa que costo un despliegue el 2026-09-09.
  // 'dashboard' esta aqui porque es quien registra el namespace
  // dashboardWidget: sin el, el registro del widget de grafica revienta por
  // namespace inexistente segun el orden en que Nuxt decida arrancar.
  //
  // 'realtime' porque aquí se registra el aviso de permisos cambiados.
  //
  // 'enterprise' por lo mismo que 'premium', y comprobado: sin declararlo, los
  // roles con candado, la pestana de Equipos y los enlaces de soporte y
  // auditoria seguian apareciendo. Se daban de baja y enterprise los volvia a
  // registrar despues, porque el orden entre los dos quedaba al azar.
  //
  // 'builder' y 'automation' porque registran su propio recorrido de
  // bienvenida, y aquí se dan de baja todos (ver más abajo).
  dependsOn: [
    'core',
    // Es quien provee $client y $clientErrorMap, que usa el interceptor de
    // la supervision.
    'client-handler',
    'database',
    'premium',
    'dashboard',
    'enterprise',
    'realtime',
    'builder',
    'automation',
  ],
  setup(nuxtApp) {
    const { $registry } = nuxtApp
    const context = { app: nuxtApp }
    $registry.register('preview', new WordMGFilePreview(context))

    // Reemplaza en vez de convivir: con los dos registrados el usuario ve el
    // mismo formato dos veces, uno funcionando y otro pidiendo pagar.
    const reemplazar = (espacio, Tipo) => {
      const tipo = Tipo.getType()
      if ($registry.exists(espacio, tipo)) {
        $registry.unregister(espacio, tipo)
      }
      $registry.register(espacio, new Tipo(context))
    }

    for (const Tipo of [
      ExcelTableExporterType,
      JSONTableExporterType,
      XMLTableExporterType,
      ArchivosTableExporterType,
    ]) {
      reemplazar('exporter', Tipo)
    }

    // Coloreado de filas: mismos nombres que premium para reemplazarlos y no
    // dejar el menu con cuatro entradas, dos de ellas con candado.
    for (const Tipo of [
      BordeIzquierdoColorViewDecoratorType,
      FondoColorViewDecoratorType,
    ]) {
      reemplazar('viewDecorator', Tipo)
    }
    for (const Tipo of [
      ColorPorSeleccionValueProviderType,
      ColorCondicionalValueProviderType,
    ]) {
      reemplazar('decoratorValueProvider', Tipo)
    }

    // Widget de grafica. Se dan de baja los de premium ("chart" y "pie_chart"):
    // el nuestro cubre los cuatro tipos en uno, y dejarlos solo llenaria el
    // selector de opciones con candado.
    for (const viejo of ['chart', 'pie_chart']) {
      if ($registry.exists('dashboardWidget', viejo)) {
        $registry.unregister('dashboardWidget', viejo)
      }
    }
    reemplazar('dashboardWidget', GraficaWidgetType)

    // Vistas personales.
    reemplazar('viewOwnershipType', VistaPersonalMGOwnershipType)
    // "Mis asuntos": la de quien solo ve sus filas. No se ofrece al crear una
    // vista; la crea el servidor. Ver extras_mg/asignados.
    reemplazar('viewOwnershipType', SoloAsignadosMGOwnershipType)

    // Vistas propias: Kanban y Calendario.
    for (const Tipo of [
      KanbanMGViewType,
      CalendarioMGViewType,
      TimelineMGViewType,
    ]) {
      reemplazar('view', Tipo)
    }

    // Modo encuesta para los formularios.
    reemplazar('formViewMode', FormViewModeEncuestaType)

    // El aviso de fila inválida de la dependencia de fechas, junto al botón de
    // abrir cada fila. Reemplaza a la de premium (que ya había reemplazado a la
    // del core), por eso 'premium' va en dependsOn. Ver applicationTypes.js.
    reemplazar('application', DatabaseMGApplicationType)

    // La barra de distribución del pie de la tabla, que es la "batería" de
    // Monday. Se suma a las agregaciones del core; no reemplaza ninguna.
    reemplazar('viewAggregation', BateriaViewAggregationType)

    // Se dan de baja los de premium que duplican algo que ya escribimos. Sin
    // esto el menu de crear vista ofrece seis opciones donde tres tienen
    // candado —y las suyas salen primero, asi que es en las que se hace clic—.
    //
    // El menu no filtra nada: ViewsContext pinta un CreateViewLink por cada
    // tipo registrado, y no hay bandera para ocultar uno. Darlos de baja es la
    // unica via.
    //
    // survey y personal no tienen ningun uso en la instancia (comprobado).
    // kanban, calendar y timeline si aparecen en 435 vistas, todas de las
    // plantillas que Baserow trae de fabrica: al abrir una de esas plantillas
    // TableTemplate.vue hace $registry.get('view', tipo) sin guarda. Es el
    // precio de tener el menu limpio, y esta medido, no supuesto.
    const darDeBaja = (espacio, tipos) => {
      for (const t of tipos) {
        if ($registry.exists(espacio, t)) {
          $registry.unregister(espacio, t)
        }
      }
    }
    darDeBaja('view', ['kanban', 'calendar', 'timeline'])
    darDeBaja('formViewMode', ['survey'])
    darDeBaja('viewOwnershipType', ['personal'])

    // Marca propia (2026-09-29): sin recorridos de bienvenida. Hablan de
    // Baserow en cada paso ("Welcome to Baserow", "Your Baserow Control
    // Center"...) y aquí la gente llega invitada a un espacio que ya existe.
    // Se quitan todos, sin lista: si una actualización trae uno nuevo, tampoco
    // sale. GuidedTour.vue pinta lo que haya en el registro.
    darDeBaja('guidedTour', Object.keys($registry.getAll('guidedTour')))
    // El onboarding ya no le sale a nadie (el backend marca las cuentas nuevas
    // como hechas, ver extras_mg/marca.py). Aun así se quita el paso 'more':
    // es el de "compartir esto con el equipo de Baserow", y su complete() es el
    // que les manda los datos.
    darDeBaja('onboarding', ['more'])

    // Sincronizar datos: sin licencia solo sirven iCal y PostgreSQL, que vienen
    // en el core (MIT). Los otros cinco (tabla de Baserow, Jira, GitHub, GitLab,
    // HubSpot) son de enterprise y salian con candado: fuera del menu, igual que
    // el resto de lo que no se puede usar. Y PostgreSQL vuelve a ser el del core:
    // el de enterprise le agrega "sincronizar en dos sentidos", que el servidor
    // rechaza sin licencia y tambien salia con candado. Ver wiki/sincronizacion.md.
    darDeBaja('dataSync', [
      'local_baserow_table',
      'jira_issues',
      'github_issues',
      'gitlab_issues',
      'hubspot_contacts',
    ])
    reemplazar('dataSync', PostgreSQLDataSyncType)
    // Programar que se sincronice sola cada cierto tiempo tambien es de
    // enterprise: la pestaña salia con candado en la configuracion de la tabla.
    darDeBaja('configureDataSync', ['periodic-interval'])

    // Webhooks avanzados: "Filas que entran a una vista", sin candado y con su
    // selector de vista. Mismo tipo que el de enterprise para reemplazarlo; el
    // backend es extras_mg/webhooks.py.
    reemplazar('webhookEvent', FilasQueEntranAVistaWebhookEventType)

    // El rol Lector: solo lectura en todo el espacio de trabajo. Quien lo hace
    // cumplir es el manager de Python del mismo nombre; esto es el nombre en la
    // lista de Miembros y el que evita ofrecer botones que el servidor niega.
    reemplazar('roles', LectorMGRoleType)
    reemplazar('permissionManager', LectorMGPermissionManagerType)

    // Bases ocultas: esconder una base entera a quien no deba verla. Es lo
    // mas parecido a los permisos por tabla de Baserow que se sostiene sin
    // reimplementar medio producto, y corta por la base porque un campo de
    // enlace no puede cruzarlas: es la frontera que el producto ya garantiza.
    reemplazar('permissionManager', BasesOcultasMGPermissionManagerType)
    reemplazar('workspaceSettingsPage', AccesoABasesWorkspaceSettingsPageType)

    // Roles por base y tabla (Encargado, Editor, Lector, Sin acceso). El
    // backend ya solo manda el objeto de 'mg_roles'; los dos de arriba se
    // quedan registrados por si algo los nombra, pero no reciben nada.
    reemplazar('permissionManager', RolesMGPermissionManagerType)
    reemplazar('roles', SinAccesoMGRoleType)

    // Cuando un admin o un Encargado cambia lo que alguien ve, el servidor le
    // avisa a esa persona: se piden sus permisos otra vez y se le ofrece
    // recargar para que la barra lateral quede al día.
    nuxtApp.$realtime?.registerEvent(
      'mg_permisos_actualizados',
      async ({ store }, { workspace_id: workspaceId }) => {
        const workspace = store.getters['workspace/get'](workspaceId)
        if (!workspace) return
        try {
          await store.dispatch('workspace/forceFetchPermissions', workspace)
        } catch (e) {}
        await store.dispatch('toast/setPermissionsUpdated', true)
      }
    )

    // Permisos por columna: quién escribe cada una. El manager hace que el
    // core ponga en solo lectura las celdas de las que no escribes; el item
    // ocupa en el menú de la columna el sitio del de enterprise (que pedía
    // licencia). Cuando alguien cambia uno, el servidor avisa a todo el
    // espacio y se piden los permisos otra vez, SIN el aviso de "recarga": lo
    // que cada quien ve no cambió, solo qué celdas edita, y eso el core lo
    // repinta en cuanto llegan.
    reemplazar('permissionManager', PermisosDeCampoMGPermissionManagerType)
    reemplazar('fieldContextItem', PermisosDeColumnaContextItemType)
    nuxtApp.$realtime?.registerEvent(
      'mg_permisos_de_campo',
      async ({ store }, { workspace_id: workspaceId }) => {
        const workspace = store.getters['workspace/get'](workspaceId)
        if (!workspace) return
        try {
          await store.dispatch('workspace/forceFetchPermissions', workspace)
        } catch (e) {}
      }
    )

    // El tiempo real de las reglas de campo del core 2.3.3 trae dos errores: el
    // servidor manda 'field_rule_created' y el frontend escucha
    // 'field_rule_added', y 'field_rule_deleted' llama a una mutación que no
    // existe (DELETE_RULE). Sin esto, si otra persona crea o quita la
    // dependencia de fechas, las pestañas abiertas no se enteran hasta recargar.
    // El aviso también le llega a quien la creó o la quitó, que ya la tiene al
    // día: por eso se mira antes de agregar o quitar.
    nuxtApp.$realtime?.registerEvent('field_rule_created', ({ store }, { rule }) => {
      const tableId = rule.table_id
      if (!store.getters['fieldRules/getRuleById']({ tableId, ruleId: rule.id })) {
        store.commit('fieldRules/ADD_RULE', { tableId, rule })
      }
    })
    nuxtApp.$realtime?.registerEvent('field_rule_deleted', ({ store }, { rule }) => {
      const tableId = rule.table_id
      if (store.getters['fieldRules/getRuleById']({ tableId, ruleId: rule.id })) {
        store.commit('fieldRules/REMOVE_RULE', { tableId, ruleId: rule.id })
      }
    })

    // El registro de auditoria en el panel de administracion. Se registra
    // DESPUES de dar de baja el 'audit-log' de enterprise (mas abajo), asi
    // que no compiten por el sitio.
    reemplazar('admin', RegistroAdminType)
    // Entrar con Google: el botón en el inicio de sesión y su panel.
    reemplazar('authProvider', GoogleMGAuthProviderType)
    reemplazar('admin', SsoAdminType)
    // La celda de rol de la tabla de Miembros: la del core decide el texto
    // con 'ADMIN o si no MEMBER', asi que a un Lector lo llamaria Miembro.
    reemplazar('membersPagePlugins', RolesMGMembersPagePluginType)

    // Lo que enterprise deja a la vista pero con candado. No es que estorbe de
    // adorno: en Miembros, los roles bloqueados salen en la MISMA lista que
    // Admin y Miembro, asi que la pregunta "que rol le pongo" se contesta entre
    // siete opciones de las que cuatro no se pueden usar. Y una seccion de
    // administracion que solo sirve para decir que se paga es ruido en el unico
    // sitio donde alguien busca algo concreto.
    //
    // Se quitan solo las que estan desactivadas sin licencia. Admin, Miembro y
    // Lector se quedan, que son las que de verdad hacen algo aqui.
    darDeBaja('roles', [
      'builder',
      'editor',
      'commenter',
      'viewer',
      'noAccess',
      'noRoleLowPriority',
    ])
    darDeBaja('admin', ['audit-log', 'data-scanner', 'auth-providers'])
    darDeBaja('workspaceSettingsPage', ['teams'])
    darDeBaja('membersPagePlugins', ['enterprise_members_columns'])

    // Reemplaza el plugin de premium para poder ofrecer el interruptor de
    // "quitar el logo" sin dejar el suyo con candado al lado. Conserva su
    // nombre de tipo porque PaidFeaturesModal lo busca por nombre.
    reemplazar('plugin', ExtrasMGPlugin)
    reemplazar('plugin', EnterpriseSinCandadosPlugin)

    // Supervision: cuando el servidor pide confirmar un cambio de miembros o
    // roles (solo a Victor e Isaac), ensena el modal y repite la peticion. En
    // el servidor de Nuxt no hace falta: ahi nadie hace clic.
    if (import.meta.client && nuxtApp.$client && nuxtApp.$clientErrorMap) {
      instalarInterceptor(nuxtApp)
    }
  },
})
